// pages/Invoice/SequencePage.tsx
import React, { useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "../ui/Button";
import { InputText } from "primereact/inputtext";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { Card } from "../../components/ui/Card";
import { InvoiceNumberSequence } from "../../types/invoiceNumberSeq.types";
import {
  getAllSequences,
  deleteSequence,
  setDefaultSequence,
  updateSequence,
  createSequence,
} from "../../service/sequence/SequenceService";
import { SequenceForm } from "./SequenceForm";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPen, faTrash } from "@fortawesome/free-solid-svg-icons";

const SequenceList: React.FC = () => {
  const [sequences, setSequences] = useState<InvoiceNumberSequence[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [lazyParams, setLazyParams] = useState({
    first: 0,
    rows: 10,
    page: 1,
    sortField: "sequence_name",
    sortOrder: undefined,
    search: "",
  });
  const [globalFilter, setGlobalFilter] = useState<string>("");
  const [selectedSequence, setSelectedSequence] =
    useState<InvoiceNumberSequence | null>(null);
  const [isFormVisible, setIsFormVisible] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const toast = useRef<any>(null);
  const dispatch = useDispatch();

  useEffect(() => {
    loadSequences();
  }, [lazyParams]);

  const loadSequences = async () => {
    const storedTenantId = localStorage.getItem("tenantId")
    const tenantId = storedTenantId ?JSON.parse(storedTenantId):null
    setLoading(true);
    try {
      const response = await getAllSequences(dispatch, {
        Page: lazyParams.page,
        Limit: lazyParams.rows,
        SortProperty: lazyParams.sortField,
        SortBy: lazyParams.sortOrder === 1 ? "ASC" : "DESC",
        Filter: globalFilter,
         ...((tenantId || tenantId === null) ? { tenantId: tenantId } : {}),
      });

      if (response) {
        setSequences(response?.List);
        setTotalRecords(response?.TotalCount || 0);
      } else {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to load sequences",
        });
      }
    } catch (error) {
      console.error("Error loading sequences:", error);
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to load sequences",
      });
    } finally {
      setLoading(false);
    }
  };

  const onPage = (event: any) => {
    setLazyParams({
      ...lazyParams,
      first: event.first,
      rows: event.rows,
      page: event.page + 1,
    });
  };

  const onSort = (event: any) => {
    setLazyParams({
      ...lazyParams,
      sortField: event.sortField,
      sortOrder: event.sortOrder,
    });
  };

  const onGlobalFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setGlobalFilter(value);
    setLazyParams({
      ...lazyParams,
      first: 0,
      page: 1,
      search: value,
    });
  };

  const handleEdit = (sequence: InvoiceNumberSequence) => {
    setSelectedSequence(sequence);
    setIsFormVisible(true);
  };

  const handleCreateNew = () => {
    setSelectedSequence(null);
    setIsFormVisible(true);
  };

  const handleCancel = () => {
    setIsFormVisible(false);
    setSelectedSequence(null);
  };

  const handleSave = async (
    formData: Omit<InvoiceNumberSequence, "sequence_id" | "last_reset_date">
  ) => {
    setIsSubmitting(true);
    try {
      let response;

      if (selectedSequence) {
        // Update existing sequence
        response = await updateSequence(
          dispatch,
          selectedSequence.sequence_id.toString(),
          formData
        );
      } else {
        // Create new sequence
        response = await createSequence(dispatch, formData);
      }

      if (response) {
        toast.current.show({
          severity: "success",
          summary: "Success",
          detail: selectedSequence
            ? "Sequence updated successfully"
            : "Sequence created successfully",
        });
        setIsFormVisible(false);
        loadSequences();
      } else {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to save sequence",
        });
      }
    } catch (error) {
      console.error("Error saving sequence:", error);
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "An error occurred while saving the sequence",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const confirmDelete = (sequence: InvoiceNumberSequence) => {
    confirmDialog({
      message: `Are you sure you want to delete "${sequence.sequence_name}"?`,
      header: "Confirm Delete",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: () => handleDelete(sequence.sequence_id.toString()),
    });
  };

  const handleDelete = async (id: string) => {
    try {
      const response = await deleteSequence(dispatch, id);
      if (response) {
        toast.current.show({
          severity: "success",
          summary: "Success",
          detail: "Sequence deleted successfully",
        });
        loadSequences();
      } else {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: response.result || "Failed to delete sequence",
        });
      }
    } catch (error) {
      console.error("Error deleting sequence:", error);
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to delete sequence",
      });
    }
  };

  const handleSetDefault = async (id: string) => {
    try {
      const response = await setDefaultSequence(dispatch, id);
      if (response) {
        toast.current.show({
          severity: "success",
          summary: "Success",
          detail: "Default sequence set successfully",
        });
        loadSequences();
      } else {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: response.result || "Failed to set default sequence",
        });
      }
    } catch (error) {
      console.error("Error setting default sequence:", error);
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to set default sequence",
      });
    }
  };

  // Formatting functions for table cells
  const formatPreview = (sequence: InvoiceNumberSequence) => {
    const paddedNumber = String(sequence.next_number).padStart(
      sequence.number_padding,
      "0"
    );
    return `${sequence.prefix || ""}${paddedNumber}${sequence.suffix || ""}`;
  };

  // Template for the action buttons
  const actionBodyTemplate = (rowData: InvoiceNumberSequence) => {
    return (
      <div className="flex gap-2 justify-end">
        <Button
          variant="secondary"
          className="actionBtn p-button-rounded p-button-text text-gold border-gold hover:bg-gold hover:text-white h-8 w-8 p-0 flex items-center justify-center"
          onClick={() => handleEdit(rowData)}
        >
          <FontAwesomeIcon icon={faPen} />
        </Button>
        {!rowData.is_default && (
          <Button
            variant="danger"
            className="actionBtn p-button-rounded p-button-text border border-red-500 text-danger hover:text-white hover:bg-red-500 h-8 w-8 p-0 flex items-center justify-center"
            onClick={() => confirmDelete(rowData)}
          >
            <FontAwesomeIcon icon={faTrash} />
          </Button>
        )}
      </div>
    );
  };

  // Template for the default status
  const defaultBodyTemplate = (rowData: InvoiceNumberSequence) => {
    if (rowData.is_default) {
      return <Tag severity="success" value="Default" />;
    }
    return (
      <Button
        // className="p-button-sm text-gold hover:text-white"
        onClick={() => handleSetDefault(rowData.sequence_id.toString())}
      >
        Set as Default
      </Button>
    );
  };

  // Template for the reset frequency
  const resetFrequencyBodyTemplate = (rowData: InvoiceNumberSequence) => {
    return <span className="capitalize">{rowData.reset_frequency}</span>;
  };

  // Header with search functionality
  const renderHeader = () => {
    return (
      <div className="flex justify-between items-center flex-wrap p-2 gap-2">
        <h2 className="text-xl font-semibold text-gray-800 mt-0 mb-0">Invoice Number Sequences</h2>
        <div className="flex items-center gap-4">
          <span className="p-input-icon-left">
            <i className="pi pi-search" />
            <InputText
              value={globalFilter}
              onChange={onGlobalFilterChange}
              placeholder="Search..."
              className="p-inputtext-sm"
            />
          </span>
          <Button
            variant="primary"
            onClick={handleCreateNew}
          >
            Create New
          </Button>
        </div>
      </div>
    );
  };

  const header = renderHeader();

  return (
    <div className="p-0">
      <Toast ref={toast} />
      <ConfirmDialog />

      {isFormVisible ? (
        <SequenceForm
          sequence={selectedSequence || undefined}
          onSave={handleSave}
          onCancel={handleCancel}
          isSubmitting={isSubmitting}
        />
      ) : (
        <div>
          <DataTable
            value={sequences}
            lazy
            dataKey="sequence_id"
            paginator
            first={lazyParams.first}
            rows={lazyParams.rows}
            totalRecords={totalRecords}
            onPage={onPage}
            onSort={onSort}
            sortField={lazyParams.sortField}
            sortOrder={lazyParams.sortOrder}
            loading={loading}
            header={header}
            emptyMessage="No sequences found"
            className="p-datatable-sm"
          >
            <Column field="sequence_name" header="Name" sortable />
            <Column
              header="Format"
              body={(rowData) => formatPreview(rowData)}
            />
            <Column
              field="next_number"
              header="Next Number"
              sortable
              className="font-mono"
            />
            <Column
              field="suffix"
              header="Suffix"
              sortable
              className="font-mono"
            />
            <Column
              field="prefix"
              header="Prefix"
              sortable
              className="font-mono"
            />
            <Column
              field="reset_frequency"
              header="Reset"
              body={resetFrequencyBodyTemplate}
              sortable
            />
            <Column header="Default" body={defaultBodyTemplate} />
            <Column body={actionBodyTemplate} style={{ width: "120px" }} />
          </DataTable>
        </div>
      )}
    </div>
  );
};

export default SequenceList;
